import { useState } from 'react'
import RoutesMap from 'route_control/RouteControl'
import { Form, InputGroup, Button, Card } from 'react-bootstrap'
import { useFormik } from 'formik'
import { ForgotPasswordStep1Schema, ForgotPasswordResetSchema } from './ValidationSchema'
import { KeyRound, UserRound, Eye, EyeOff, ArrowLeft, ShieldCheck } from 'lucide-react'
import { toast } from 'react-toastify'
import logo from 'assets/images/express-logo.png'
import { ButtonLoader } from 'components/loader'
import { useNavigate } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import { forgotPasswordSendOtp, forgotPasswordVerifyOtp, forgotPasswordReset } from '../../../redux/slices/assets'
import { isApiSuccess, apiMessage } from 'utils'

// Portal flow: username -> OTP (sent to registered email) -> new password
const STEPS = [
  { key: 'account', label: 'Account' },
  { key: 'otp', label: 'Verify OTP' },
  { key: 'password', label: 'New password' },
]

export default function ForgotPasswordForm(props) {

  const { setTogggle } = props

  const [step, setStep] = useState(0)
  const [spinner, setSpinner] = useState(false)
  const [otp, setOtp] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)

  // username + ROTP returned by validateForgotPassword (needed for the reset call)
  const [ctx, setCtx] = useState({ username: '', rotp: '' })

  const dispatch = useDispatch()
  const navigate = useNavigate()

  const step1Formik = useFormik({
    initialValues: { username: '' },
    validationSchema: ForgotPasswordStep1Schema,

    onSubmit: (values) => {
      setSpinner(true)
      dispatch(forgotPasswordSendOtp({ username: values.username.trim() })).then((res) => {
        setSpinner(false)
        const payload = res?.payload
        if (isApiSuccess(payload)) {
          setCtx({ username: values.username.trim(), rotp: '' })
          toast.success('Receive your OTP on mail', { position: 'bottom-right' })
          setOtp('')
          setStep(1)
        } else {
          // e.g. responseCode 404 -> responseMessage
          toast.error(apiMessage(payload, 'Something went wrong!!..'), { position: 'bottom-right' })
        }
      })
    },
  })

  const resetFormik = useFormik({
    initialValues: { newPassword: '', confirmPassword: '' },
    validationSchema: ForgotPasswordResetSchema,

    onSubmit: (values, action) => {
      setSpinner(true)
      dispatch(forgotPasswordReset({
        newpassword: values.newPassword,
        username: ctx.username,
        rtop: ctx.rotp,
      })).then((res) => {
        setSpinner(false)
        const payload = res?.payload
        if (isApiSuccess(payload)) {
          toast.success('Password Successfully changed', { position: 'bottom-right' })
          action.resetForm()
          setOtp('')
          setStep(0)
          setTogggle(false)
          navigate(RoutesMap.LOGIN.path)
        } else {
          toast.error(apiMessage(payload, 'Something went wrong!!..'), { position: 'bottom-right' })
        }
      })
    },
  })

  const handleVerifyOtp = () => {
    if (!otp.trim()) {
      toast.error('Please provide OTP', { position: 'bottom-right' })
      return
    }
    setSpinner(true)
    dispatch(forgotPasswordVerifyOtp({ username: ctx.username, otp: otp.trim() })).then((res) => {
      setSpinner(false)
      const payload = res?.payload
      if (isApiSuccess(payload)) {
        setCtx({ ...ctx, rotp: payload?.data?.data?.ROTP })
        setStep(2)
        resetFormik.resetForm()
      } else if (payload?.data?.responseCode === 417) {
        toast.error('Invalid OTP', { position: 'bottom-right' })
      } else {
        toast.error(apiMessage(payload, 'Something went wrong!!..'), { position: 'bottom-right' })
      }
    })
  }

  const handleResendOtp = () => {
    if (!ctx.username) return
    setSpinner(true)
    dispatch(forgotPasswordSendOtp({ username: ctx.username })).then((res) => {
      setSpinner(false)
      if (isApiSuccess(res?.payload)) {
        toast.success('Receive your OTP on mail', { position: 'bottom-right' })
        setOtp('')
      } else {
        toast.error(apiMessage(res?.payload, 'Something went wrong!!..'), { position: 'bottom-right' })
      }
    })
  }

  const goBackToLogin = () => {
    setStep(0)
    setOtp('')
    step1Formik.resetForm()
    resetFormik.resetForm()
    setTogggle(false)
  }

  const stepIndicator = (
    <div className="d-flex justify-content-center align-items-center gap-1 mb-3 flex-wrap">
      {STEPS.map((s, i) => (
        <span key={s.key} className="d-flex align-items-center">
          <span
            className="rounded-pill px-2 py-1"
            style={{
              fontSize: '11px',
              fontWeight: 600,
              backgroundColor: i === step ? '#006972' : i < step ? '#e8eeed' : '#f1f3f5',
              color: i === step ? '#fff' : '#6c757d',
            }}
          >
            {i + 1}. {s.label}
          </span>
          {i < STEPS.length - 1 ? <span className="text-muted mx-1" style={{ fontSize: '10px' }}>›</span> : null}
        </span>
      ))}
    </div>
  )

  const passwordField = (name, placeholder, show, setShow) => (
    <>
      <InputGroup
        className="rounded-3 shadow-sm"
        style={{ border: '1px solid #e1e5eb', overflow: 'hidden', backgroundColor: '#f9fafb' }}
      >
        <InputGroup.Text className="bg-transparent border-0 text-muted px-3">
          <KeyRound size={18} strokeWidth={2} />
        </InputGroup.Text>
        <Form.Control
          type={show ? 'text' : 'password'}
          className="login-input border-0 bg-transparent shadow-none px-0"
          placeholder={placeholder}
          name={name}
          value={resetFormik.values[name]}
          onChange={resetFormik.handleChange}
          onBlur={resetFormik.handleBlur}
          style={{ fontSize: '14px', height: '45px' }}
        />
        <InputGroup.Text
          className="bg-transparent border-0 text-muted px-3"
          style={{ cursor: 'pointer' }}
          onClick={() => setShow(!show)}
        >
          {show ? <EyeOff size={18} /> : <Eye size={18} />}
        </InputGroup.Text>
      </InputGroup>
      {resetFormik.errors[name] && resetFormik.touched[name] ? (
        <div className="text-danger mt-1 ms-1" style={{ fontSize: '12px' }}>
          {resetFormik.errors[name]}
        </div>
      ) : null}
    </>
  )

  return (
    <Card className="h-100 border-0 shadow" style={{ borderRadius: '16px', backgroundColor: '#ffffff', overflow: 'hidden' }}>
      <Form
        className="h-100 d-flex flex-column"
        onSubmit={(e) => {
          e.preventDefault()
          if (step === 0) step1Formik.handleSubmit()
          if (step === 1) handleVerifyOtp()
          if (step === 2) resetFormik.handleSubmit()
        }}
      >
        <div className="text-start px-4 pt-3">
          <Button
            variant="link"
            className="p-0 text-decoration-none text-muted d-inline-flex align-items-center gap-1"
            style={{ fontSize: '13px' }}
            type="button"
            onClick={goBackToLogin}
          >
            <ArrowLeft size={16} />
            Back To Login
          </Button>
        </div>

        <Card.Body className="px-4 px-sm-5 d-flex flex-column justify-content-center pb-4">
          <div className="text-center mb-2">
            <img src={logo} alt="logo" className="img-fluid mb-2" style={{ height: '60px', objectFit: 'contain' }} />
            <h4 className="fw-bold mb-1" style={{ color: '#001529' }}>
              Forgot password
            </h4>
            <p className="text-muted" style={{ fontSize: '14px' }}>
              {step === 0 && 'Enter your username. We will send an OTP to your registered email.'}
              {step === 1 && `Enter the OTP sent to the email registered for ${ctx.username}.`}
              {step === 2 && 'Choose a new password for your account.'}
            </p>
          </div>

          {stepIndicator}

          {step === 0 && (
            <>
              <div className="mb-2">
                <Form.Label className="fw-semibold mb-1 text-dark" style={{ fontSize: '13px' }}>
                  Username
                </Form.Label>
                <InputGroup
                  className="rounded-3 shadow-sm"
                  style={{ border: '1px solid #e1e5eb', overflow: 'hidden', backgroundColor: '#f9fafb' }}
                >
                  <InputGroup.Text className="bg-transparent border-0 text-muted px-3">
                    <UserRound size={18} strokeWidth={2} />
                  </InputGroup.Text>
                  <Form.Control
                    className="login-input border-0 bg-transparent shadow-none px-0"
                    placeholder="Enter username"
                    name="username"
                    value={step1Formik.values.username}
                    onChange={step1Formik.handleChange}
                    onBlur={step1Formik.handleBlur}
                    style={{ fontSize: '14px', height: '45px' }}
                  />
                </InputGroup>
                {step1Formik.errors.username && step1Formik.touched.username ? (
                  <div className="text-danger mt-1 ms-1" style={{ fontSize: '12px' }}>
                    {step1Formik.errors.username}
                  </div>
                ) : null}
              </div>

              <div className="mt-2 text-center">
                <Button
                  type="submit"
                  className="w-100 login_save_btn shadow text-uppercase fw-bold rounded-3"
                  style={{ letterSpacing: '1px' }}
                  disabled={spinner}
                >
                  {spinner ? <ButtonLoader /> : 'Send OTP'}
                </Button>
              </div>
            </>
          )}

          {step === 1 && (
            <>
              <div className="mb-2">
                <Form.Label className="fw-semibold mb-1 text-dark" style={{ fontSize: '13px' }}>
                  OTP
                </Form.Label>
                <InputGroup
                  className="rounded-3 shadow-sm"
                  style={{ border: '1px solid #e1e5eb', overflow: 'hidden', backgroundColor: '#f9fafb' }}
                >
                  <InputGroup.Text className="bg-transparent border-0 text-muted px-3">
                    <ShieldCheck size={18} strokeWidth={2} />
                  </InputGroup.Text>
                  <Form.Control
                    className="login-input border-0 bg-transparent shadow-none px-0"
                    placeholder="Enter OTP"
                    inputMode="numeric"
                    name="otp"
                    value={otp}
                    onChange={(e) => setOtp(e.target.value)}
                    style={{ fontSize: '14px', height: '45px' }}
                  />
                </InputGroup>
              </div>

              <div className="text-center mb-2">
                <Button variant="link" className="p-0 text-decoration-none" style={{ fontSize: '13px' }} type="button" onClick={handleResendOtp} disabled={spinner}>
                  Resend OTP
                </Button>
              </div>

              <div className="d-flex flex-column gap-2">
                <Button
                  type="submit"
                  className="w-100 login_save_btn shadow text-uppercase fw-bold rounded-3"
                  style={{ letterSpacing: '1px' }}
                  disabled={spinner}
                >
                  {spinner ? <ButtonLoader /> : 'Verify'}
                </Button>
                <Button
                  type="button"
                  variant="outline-secondary"
                  className="w-100 rounded-3 fw-semibold"
                  style={{ height: '45px' }}
                  disabled={spinner}
                  onClick={() => {
                    setStep(0)
                    setOtp('')
                    step1Formik.setValues({ username: ctx.username })
                  }}
                >
                  Back
                </Button>
              </div>
            </>
          )}

          {step === 2 && (
            <>
              <div className="mb-2">
                <Form.Label className="fw-semibold mb-1 text-dark" style={{ fontSize: '13px' }}>
                  New Password
                </Form.Label>
                {passwordField('newPassword', 'Enter new password', showPassword, setShowPassword)}
              </div>

              <div className="mb-2">
                <Form.Label className="fw-semibold mb-1 text-dark" style={{ fontSize: '13px' }}>
                  Confirm Password
                </Form.Label>
                {passwordField('confirmPassword', 'Confirm new password', showConfirmPassword, setShowConfirmPassword)}
              </div>

              <div className="d-flex flex-column gap-2 mt-2">
                <Button
                  type="submit"
                  className="w-100 login_save_btn shadow text-uppercase fw-bold rounded-3"
                  style={{ letterSpacing: '1px' }}
                  disabled={spinner}
                >
                  {spinner ? <ButtonLoader /> : 'Change password'}
                </Button>
              </div>
            </>
          )}
        </Card.Body>

        <Card.Footer className="bg-light border-top-0 px-4 py-2 text-center">
          <span style={{ fontSize: '12px' }} className="text-muted">
            For security, never share your OTP with anyone.
          </span>
        </Card.Footer>
      </Form>
    </Card>
  )
}
